import * as s from "@gromia/components/theme/t.module.css";
import { genericJsonLd, JSONLD } from "@gromia/utils/jsonld";
import { TxContext, useCms } from "@gromia/utils/useString";
import cx from "classnames";
import { pipe } from "fp-ts/lib/function";
import * as O from "fp-ts/lib/Option";
import * as R from "fp-ts/lib/Record";
import { graphql, useStaticQuery } from "gatsby";
import React from "react";
import { Helmet } from "react-helmet";

const query = graphql`
  query SEO {
    site {
      siteMetadata {
        titleTemplate
        siteUrl
      }
    }
  }
`;

type Props = {
  title: TxContext;
  description: TxContext | null;
  image?: string;
  article?: {
    section: string;
    publishedAt: string;
    modifiedAt: string;
  };
  bodyAttributes?: Record<string, string>;
  prev?: string;
  next?: string;
  jsonld?: JSONLD | null | (JSONLD | null)[];
  noindex?: boolean;
  nofollow?: boolean;
};

interface QueryResult {
  site: {
    siteMetadata: {
      titleTemplate: string;
      siteUrl: string;
    };
  };
}

function replaceAll(src: string, dst: string) {
  return (s: string) => s.replace(new RegExp(src, "g"), dst);
}

function removeStyling(text: string): string {
  return pipe(text, replaceAll("<b>", ""), replaceAll("</b>", ""));
}

export const Seo = React.memo((props: Props) => {
  const cms = useCms();
  const result: QueryResult = useStaticQuery(query);
  const data = result.site.siteMetadata;
  const title = removeStyling(props.title.raw);
  const description = pipe(
    props.description,
    O.fromNullable,
    O.map(x => removeStyling(x.raw)),
    O.toNullable,
  );

  const facebook: Record<string, string> = {
    "fb:app_id": "157634741097712",
    "og:locale": "it_IT",
    "og:title": title,
    "og:site_name": "Gromia",
  };

  const twitter: Record<string, string> = {
    "twitter:card": "summary_large_image",
    "twitter:creator": "@gromiaapp",
    "twitter:title": title,
  };

  const generic: Record<string, string> = {
    robots: [
      props.noindex ? "noindex" : "index",
      props.nofollow ? "nofollow" : "follow",
      "max-snippet:-1",
      "max-image-preview:large",
      "max-video-preview:-1",
    ]
      .filter(x => x)
      .join(", "),
  };

  if (description) {
    Object.assign(generic, {
      description,
    });
    Object.assign(facebook, {
      "og:description": description,
    });
    Object.assign(twitter, {
      "twitter:description": description,
    });
  }

  if (props.article) {
    Object.assign(facebook, {
      "og:type": "article",
      "article:publisher": "https://www.facebook.com/gromiaapp",
      "article:section": props.article.section,
      "article:published_time": props.article.publishedAt,
      "article:modified_time": props.article.modifiedAt,
      "og:updated_time": props.article.modifiedAt,
    });
  }

  if (props.image) {
    Object.assign(facebook, {
      "og:image": props.image,
      "og:image:secure_url": props.image,
    });
    Object.assign(twitter, {
      "twitter:image": props.image,
    });
  }

  const jsonld = [props.jsonld].flat().filter(Boolean) as JSONLD[];

  if (jsonld.length < 1) {
    jsonld.push(genericJsonLd(props.title.raw, props.description?.raw));
  }

  return (
    <React.Fragment>
      <Helmet
        title={title}
        titleTemplate={data.titleTemplate}
        bodyAttributes={props.bodyAttributes}
      >
        {props.prev ? <link rel="prev" href={props.prev} /> : null}
        {props.next ? <link rel="next" href={props.next} /> : null}
        {pipe(
          generic,
          R.mapWithIndex((k, v) => <meta key={k} name={k} content={v} />),
          Object.values,
        )}
        {pipe(
          twitter,
          R.mapWithIndex((k, v) => <meta key={k} name={k} content={v} />),
          Object.values,
        )}
        {pipe(
          facebook,
          R.mapWithIndex((k, v) => <meta key={k} property={k} content={v} />),
          Object.values,
        )}
        {jsonld.map((json, index) => (
          <script key={`jsonld-${index}`} type="application/ld+json">
            {JSON.stringify(json, null, 0)}
          </script>
        ))}
      </Helmet>
      {cms
        ? (
          <div
            className={cx(
              s.positionFixed,
              s.bottom0,
              s.left0,
              s.right0,
              s.textDark,
              s.dFlex,
              s.flexColumn,
              s.p2,
              s.zIndex4,
              s.fontSizeSmall1,
            )}
          >
            <div className={s.p2}>{props.title.element}</div>
            {props.description
              ? <div className={s.p2}>{props.description.element}</div>
              : null}
          </div>
        )
        : null}
    </React.Fragment>
  );
});
